import { useState, useMemo, useCallback } from "react";
import "./BulkUpload.scss";

// ─── Constants ────────────────────────────────────────────────────────────────

const ROLE_MAP = { 1: "Student", 2: "Teacher", 3: "Admin" };

const getMetricCards = (data) => [
  {
    label: "Total",
    value:
      (data?.summary?.csv_student_total_count ?? 0) + (data?.summary?.csv_teacher_total_count
 ?? 0),
    modifier: "gray",
  },
  {
    label: "Created",
    value: data.summary?.total_created ?? 0,
    modifier: "green",
  },
  {
    label: "Updated",
    value: data.summary?.total_updated ?? 0,
    modifier: "blue",
  },
  {
    label: "No change",
    value: data.summary?.unchanged_users ?? 0,
    modifier: "gray",
  },
  {
    label: "Total Students",
    value: data?.summary?.csv_student_total_count ?? 0,
    modifier: "purple",
  },
  {
    label: "Total Teachers",
    value: data?.summary?.csv_teacher_total_count
 ?? 0,
    modifier: "teal",
  },
];

// ─── Helpers ──────────────────────────────────────────────────────────────────

const normalise = (val = "") => String(val).toLowerCase().trim();

const matchesQuery = (fields, query) => {
  if (!query) return true;
  const q = normalise(query);
  return fields.some((f) => normalise(f).includes(q));
};

const getRoleModifier = (roleId) =>
  ({ 1: "student", 2: "teacher", 3: "admin" })[roleId] ?? "unknown";

// ─── Icons ────────────────────────────────────────────────────────────────────

function CheckCircleIcon() {
  return (
    <svg
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth={2.2}
      strokeLinecap="round"
      strokeLinejoin="round"
    >
      <path d="M22 11.08V12a10 10 0 1 1-5.93-9.14" />
      <polyline points="22 4 12 14.01 9 11.01" />
    </svg>
  );
}

function SearchIcon() {
  return (
    <svg
      width={15}
      height={15}
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth={2}
      strokeLinecap="round"
      strokeLinejoin="round"
    >
      <circle cx="11" cy="11" r="8" />
      <line x1="21" y1="21" x2="16.65" y2="16.65" />
    </svg>
  );
}

function ChevronIcon({ isOpen }) {
  return (
    <svg
      className={`crt-accordion__chevron${isOpen ? " crt-accordion__chevron--open" : ""}`}
      width={16}
      height={16}
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth={2.2}
      strokeLinecap="round"
      strokeLinejoin="round"
    >
      <polyline points="6 9 12 15 18 9" />
    </svg>
  );
}

// ─── Small pieces ─────────────────────────────────────────────────────────────

function RoleBadge({ roleId }) {
  return (
    <span className={`crt-role crt-role--${getRoleModifier(roleId)}`}>
      {ROLE_MAP[roleId] ?? "Unknown"}
    </span>
  );
}

function StatusIndicator({ active }) {
  return (
    <span
      className={`crt-status crt-status--${active ? "active" : "inactive"}`}
    >
      <span className="crt-status__dot" />
      {active ? "Active" : "Inactive"}
    </span>
  );
}

function FieldTags({ fields = [] }) {
  return (
    <div className="crt-field-tags">
      {fields.map((f) => (
        <span key={f} className="crt-field-tag">
          {f.replace(/_/g, " ")}
        </span>
      ))}
    </div>
  );
}

function EmptyRow({ colSpan }) {
  return (
    <tr>
      <td colSpan={colSpan} className="crt-empty">
        No users match your search.
      </td>
    </tr>
  );
}

// ─── Accordion ────────────────────────────────────────────────────────────────

function Accordion({
  id,
  label,
  count,
  dotModifier,
  badgeModifier,
  isOpen,
  onToggle,
  children,
}) {
  return (
    <div className="crt-accordion">
      <button
        id={`crt-trigger-${id}`}
        className="crt-accordion__trigger"
        onClick={() => onToggle(id)}
        aria-expanded={isOpen}
        aria-controls={`crt-body-${id}`}
      >
        <div className="crt-accordion__trigger-left">
          <span
            className={`crt-accordion__dot crt-accordion__dot--${dotModifier}`}
          />
          <span className="crt-accordion__title">{label}</span>
          <span className={`crt-badge crt-badge--${badgeModifier}`}>
            {count} user{count !== 1 ? "s" : ""}
          </span>
        </div>
        <ChevronIcon isOpen={isOpen} />
      </button>

      {isOpen && (
        <div
          id={`crt-body-${id}`}
          className="crt-accordion__body"
          role="region"
          aria-labelledby={`crt-trigger-${id}`}
          // ← no inline style here, SCSS handles it
        >
          {children}
        </div>
      )}
    </div>
  );
}

// ─── Table sections ───────────────────────────────────────────────────────────

function CreatedTable({ rows }) {
  return (
    <div className="crt-table-scroll">
      <table className="crt-table">
        <colgroup>
          <col style={{ width: 44 }} />
          <col style={{ width: 90 }} />
          <col style={{ width: 130 }} />
          <col style={{ width: 130 }} />
          <col style={{ width: 90 }} />
          <col style={{ width: 80 }} />
          <col style={{ width: 80 }} />
          <col style={{ width: 86 }} />
        </colgroup>
        <thead>
          <tr>
            <th>#</th>
            <th>User ID</th>
            <th>Email</th>
            <th>Name</th>
            <th>Role</th>
            <th>Class</th>
            <th>Division</th>
            <th>Status</th>
          </tr>
        </thead>
        <tbody>
          {rows.length === 0 ? (
            <EmptyRow colSpan={8} />
          ) : (
            rows.map((u, i) => {
              const fullName = [u.firstname, u.lastname]
                .filter(Boolean)
                .join(" ");
              const isActive = u.account_status === "active";
              return (
                <tr key={u.user_id}>
                  <td className="col-idx">{i + 1}</td>
                  <td>
                    <span className="crt-mono">#{u.user_id}</span>
                  </td>
                  <td>{u.email}</td>
                  <td style={{ textTransform: "capitalize" }}>
                    {fullName || "—"}
                  </td>
                  <td>
                    <span
                      className={`crt-role crt-role--${u.role_name ?? "unknown"}`}
                    >
                      {u.role_name
                        ? u.role_name.charAt(0).toUpperCase() +
                          u.role_name.slice(1)
                        : "—"}
                    </span>
                  </td>
                  <td>{u.class ?? "—"}</td>
                  <td>{u.division ?? "—"}</td>
                  <td>
                    <StatusIndicator active={isActive} />
                  </td>
                </tr>
              );
            })
          )}
        </tbody>
      </table>
    </div>
  );
}

function UpdatedTable({ rows }) {
  return (
    <div className="crt-table-scroll">
      <table className="crt-table">
        <colgroup>
          <col style={{ width: 44 }} />
          <col style={{ width: 130 }} />
          <col />
          <col style={{ width: 510 }} />
        </colgroup>
        <thead>
          <tr>
            <th>#</th>
            <th>User ID</th>
            <th>Email</th>
            <th>Updated fields</th>
          </tr>
        </thead>
        <tbody>
          {rows.length === 0 ? (
            <EmptyRow colSpan={5} />
          ) : (
            rows.map((u, i) => (
              <tr key={u.user_id}>
                <td className="col-idx">{i + 1}</td>
                <td>
                  <span className="crt-mono">#{u.user_id}</span>
                </td>
                <td>{u.email}</td>
                <td>
                  <FieldTags fields={u.updated_fields} />
                </td>
                {/* <td className="col-idx">Row {u.row}</td> */}
              </tr>
            ))
          )}
        </tbody>
      </table>
    </div>
  );
}

function UnchangedTable({ rows }) {
  return (
    <div className="crt-table-scroll">
      <table className="crt-table">
        <colgroup>
          <col style={{ width: 44 }} />
          <col style={{ width: 90 }} />
          <col style={{ width: 130 }} />
          <col style={{ width: 130 }} />
          <col style={{ width: 90 }} />
          <col style={{ width: 80 }} />
          <col style={{ width: 80 }} />
          <col style={{ width: 86 }} />
        </colgroup>
        <thead>
          <tr>
            <th>#</th>
            <th>User ID</th>
            <th>Email</th>
            <th>Name</th>
            <th>Role</th>
            <th>Class</th>
            <th>Division</th>
            <th>Status</th>
          </tr>
        </thead>
        <tbody>
          {rows.length === 0 ? (
            <EmptyRow colSpan={9} />
          ) : (
            rows.map((u, i) => {
              const d = u.unchanged_users_data ?? {};
              const fullName = [d.firstname, d.lastname]
                .filter(Boolean)
                .join(" ");
              const isActive = d.account_status === "active";
              return (
                <tr key={u.user_id}>
                  <td className="col-idx">{i + 1}</td>
                  <td>
                    <span className="crt-mono">#{u.user_id}</span>
                  </td>
                  <td>{u.email}</td>
                  <td style={{ textTransform: "capitalize" }}>
                    {fullName || "—"}
                  </td>
                  <td>
                    <span
                      className={`crt-role crt-role--${d.role_name ?? "unknown"}`}
                    >
                      {d.role_name
                        ? d.role_name.charAt(0).toUpperCase() +
                          d.role_name.slice(1)
                        : "—"}
                    </span>
                  </td>
                  <td>{d.class ?? "—"}</td>
                  <td>{d.division ?? "—"}</td>
                  <td>
                    <StatusIndicator active={isActive} />
                  </td>
                  {/* <td className="col-idx">Row {u.row}</td> */}
                </tr>
              );
            })
          )}
        </tbody>
      </table>
    </div>
  );
}

// ─── Response Table ─────────────────────────────────────────────────────────────────────

export default function CSVResponseTable({ data = {} }) {
  const [openAccordions, setOpenAccordions] = useState({});
  const [query, setQuery] = useState("");

  const toggleAccordion = useCallback((id) => {
    setOpenAccordions((prev) => ({ ...prev, [id]: !prev[id] }));
  }, []);

  const {
    created_users = [],
    updated_users = [],
    unchanged_users = [],
  } = data;

  const filteredUnchanged = useMemo(
    () =>
      unchanged_users.filter((u) => {
        const d = u.unchanged_users_data ?? {};
        const fullName = [d.firstname, d.lastname].filter(Boolean).join(" ");
        return matchesQuery(
          [
            u.email,
            String(u.user_id),
            fullName,
            d.role_name,
            d.class,
            d.division,
          ],
          query,
        );
      }),
    [unchanged_users, query],
  );

  const filteredCreated = useMemo(
    () =>
      created_users.filter((u) =>
        matchesQuery(
          [
            u.email,
            u.user_id,
            u.firstname,
            u.lastname,
            u.role_name,
            u.class,
            u.division,
          ],
          query,
        ),
      ),
    [created_users, query],
  );

  const filteredUpdated = useMemo(
    () =>
      updated_users.filter((u) =>
        matchesQuery([u.email, u.user_id, ...(u.updated_fields ?? [])], query),
      ),
    [updated_users, query],
  );

  const totalFiltered =
    filteredCreated.length + filteredUpdated.length + filteredUnchanged.length;

  return (
    <div className="csv-response-table">
      <div className="crt-header">
        <div className="crt-header__left">
          <div className="crt-header__icon-wrap">
            <CheckCircleIcon />
          </div>
          <div className="crt-header__text">
            <h2 className="crt-header__title">Upload Summary</h2>
            <p className="crt-header__subtitle">
              Review created, updated and unchanged users
            </p>
          </div>
        </div>

        <div className="crt-search">
          <span className="crt-search__icon" aria-hidden="true">
            <SearchIcon />
          </span>
          <input
            type="text"
            className="crt-search__input"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder="Search by name, email, role…"
            aria-label="Search users"
          />
          {query && (
            <span className="crt-search__count" aria-live="polite">
              {totalFiltered} result{totalFiltered !== 1 ? "s" : ""}
            </span>
          )}
        </div>
      </div>

      {/* ── Metric strip ───────────────────────────────────────────────────── */}
      <div className="crt-metrics">
        {getMetricCards(data).map(({ label, value, modifier }) => (
          <div key={label} className="crt-metric">
            <p className="crt-metric__label">{label}</p>
            <p className={`crt-metric__value crt-metric__value--${modifier}`}>
              {value}
            </p>
          </div>
        ))}
      </div>

      {/* ── Body ───────────────────────────────────────────────────────────── */}
      <div className="crt-body">
        {/* Accordions */}
        <div className="crt-accordion-list">
          <Accordion
            id="created"
            label="Newly created users"
            count={filteredCreated.length}
            dotModifier="green"
            badgeModifier="green"
            isOpen={!!openAccordions.created}
            onToggle={toggleAccordion}
          >
            <CreatedTable rows={filteredCreated} />
          </Accordion>

          <Accordion
            id="updated"
            label="Updated users"
            count={filteredUpdated.length}
            dotModifier="blue"
            badgeModifier="blue"
            isOpen={!!openAccordions.updated}
            onToggle={toggleAccordion}
          >
            <UpdatedTable rows={filteredUpdated} />
          </Accordion>

          <Accordion
            id="existing"
            label="Existing users (no change)"
            count={filteredUnchanged.length}
            dotModifier="gray"
            badgeModifier="gray"
            isOpen={!!openAccordions.existing}
            onToggle={toggleAccordion}
          >
            <UnchangedTable rows={filteredUnchanged} />
          </Accordion>
        </div>
      </div>
    </div>
  );
}
